import type { FC, ReactNode } from 'react'

import { useRef, useState } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { ArrowRight } from 'lucide-react'

import { cn } from '@/lib/utils'
import { gsap } from '@/lib/gsap'
import { useIsomorphicLayoutEffect } from '@/hooks/use-isomorphic-layout-effect'
import { SplitText } from '@/components/motion/split-text'
import { author } from '@/constants/site'
import { faqs } from '@/constants/content'

/**
 * Accordion with a measured height tween, so each panel opens to its own
 * content height rather than to a guessed maximum. Built on buttons and
 * aria-expanded rather than details/summary, so the open and close motion can
 * be choreographed to match the rest of the site.
 */
export const Faq: FC = (): ReactNode => {
    const [open, setOpen] = useState<number | null>(0)
    const root = useRef<HTMLElement>(null)

    useIsomorphicLayoutEffect(() => {
        const element = root.current

        if (!element) {
            return
        }

        const context = gsap.context(() => {
            gsap.set(element.querySelectorAll('[data-reveal]'), { visibility: 'visible' })

            if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
                return
            }

            gsap.fromTo(
                element.querySelectorAll('[data-faq-row]'),
                { y: 30, opacity: 0 },
                {
                    y: 0,
                    opacity: 1,
                    duration: 0.85,
                    ease: 'power3.out',
                    stagger: 0.06,
                    scrollTrigger: {
                        trigger: element,
                        start: 'top 76%',
                        once: true
                    }
                }
            )
        }, element)

        return () => context.revert()
    }, [])

    // Each panel animates from its current height to its natural height, then
    // is released to auto so it stays correct if the text reflows.
    useIsomorphicLayoutEffect(() => {
        const element = root.current

        if (!element) {
            return
        }

        const panels = element.querySelectorAll<HTMLElement>('[data-faq-panel]')
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches

        panels.forEach((panel, index) => {
            const expanded = index === open
            const inner = panel.firstElementChild as HTMLElement | null

            if (reduced) {
                gsap.set(panel, { height: expanded ? 'auto' : 0, opacity: expanded ? 1 : 0 })

                return
            }

            gsap.to(panel, {
                height: expanded ? panel.scrollHeight : 0,
                duration: 0.7,
                ease: 'power3.inOut',
                overwrite: true,
                onComplete: () => {
                    if (expanded) {
                        gsap.set(panel, { height: 'auto' })
                    }
                }
            })

            if (inner) {
                gsap.to(inner, {
                    opacity: expanded ? 1 : 0,
                    y: expanded ? 0 : -8,
                    duration: expanded ? 0.6 : 0.3,
                    delay: expanded ? 0.12 : 0,
                    ease: 'power2.out',
                    overwrite: true
                })
            }
        })
    }, [open])

    return (
        <section ref={root} id='faq' className='edge bg-background py-20 md:py-28'>
            <div className='mx-auto max-w-[110rem]'>
                <p className='label mb-10 text-muted-foreground'>FAQ</p>

                <div className='grid gap-10 lg:grid-cols-[minmax(0,0.72fr)_minmax(0,1.28fr)] lg:gap-16'>
                    {/* The reference pairs the accordion with a personal card
                        rather than leaving the column empty. */}
                    <aside
                        data-faq-row
                        className='card-soft h-fit p-7 lg:sticky lg:top-28'
                    >
                        <div className='flex items-center gap-3'>
                            <span className='relative size-10 overflow-hidden rounded-full'>
                                <Image
                                    src={author.portrait}
                                    alt=''
                                    fill
                                    sizes='40px'
                                    className='object-cover'
                                />
                            </span>

                            <span className='text-[0.75rem] leading-tight'>
                                <span className='block font-medium'>{author.name}</span>
                                <span className='block text-muted-foreground'>{author.role}</span>
                            </span>
                        </div>

                        <p className='mt-7 text-[0.9375rem] font-medium'>
                            Didn’t find your answer?
                        </p>

                        <p className='mt-2 text-[0.8125rem] leading-relaxed text-muted-foreground'>
                            If you have another question, or want more detail on anything
                            above, just ask. I would rather answer it now than have you
                            wondering about it on the day.
                        </p>

                        <Link
                            href='/contact'
                            className='pill mt-7 bg-foreground text-background hover:bg-foreground/85'
                        >
                            Ask a question
                            <ArrowRight className='size-4' aria-hidden='true' />
                        </Link>
                    </aside>

                    <div>
                        <SplitText
                            as='h2'
                            lines={['Any questions?']}
                            className='display-xl mb-10'
                        />

                        <dl>
                        {faqs.map((item, index) => {
                            const expanded = index === open

                            return (
                                <div key={item.question} data-faq-row className='border-b border-border'>
                                    <dt>
                                        <button
                                            type='button'
                                            onClick={() => setOpen(expanded ? null : index)}
                                            aria-expanded={expanded}
                                            aria-controls={`faq-panel-${index}`}
                                            id={`faq-trigger-${index}`}
                                            className='group flex w-full items-center justify-between gap-6 py-6 text-left'
                                        >
                                            <span
                                                className={cn(
                                                    'text-[0.9375rem] font-medium transition-colors duration-500 md:text-base',
                                                    expanded
                                                        ? 'text-foreground'
                                                        : 'text-foreground/70 group-hover:text-foreground'
                                                )}
                                            >
                                                {item.question}
                                            </span>

                                            <span
                                                aria-hidden='true'
                                                className='relative block h-4 w-4 shrink-0'
                                            >
                                                <span className='absolute left-0 top-1/2 h-px w-full -translate-y-1/2 bg-current' />
                                                <span
                                                    className={cn(
                                                        'absolute left-1/2 top-0 h-full w-px -translate-x-1/2 bg-current transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]',
                                                        expanded ? 'scale-y-0' : 'scale-y-100'
                                                    )}
                                                />
                                            </span>
                                        </button>
                                    </dt>

                                    <dd
                                        id={`faq-panel-${index}`}
                                        role='region'
                                        aria-labelledby={`faq-trigger-${index}`}
                                        data-faq-panel
                                        className='h-0 overflow-hidden'
                                    >
                                        <p className='max-w-2xl pb-8 pr-10 text-sm leading-relaxed text-muted-foreground'>
                                            {item.answer}
                                        </p>
                                    </dd>
                                </div>
                            )
                        })}
                        </dl>
                    </div>
                </div>
            </div>
        </section>
    )
}
